import { Editor, type Path, Range, Text, Transforms } from 'slate';
import {
  type MarkdownConvertOptions,
  type MarkdownRevertOptions,
} from '../../types/aha-editor';
import History from '../../collaboration/History';

function escapeRegex(char: string) {
  return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function markdownRegexpForMark(chars: string) {
  const negated =
    chars.length === 2 && chars[0] === chars[1] ? chars[0] : chars;
  const escaped = escapeRegex(chars);
  return new RegExp(
    `(?:^|\\s)(${escaped}([^\\s${negated}](?:[^${negated}]*[^\\s${negated}])?)${escaped})$`
  );
}

export function getTextUpToCursor(editor: Editor): string | undefined {
  const { selection } = editor;

  if (!selection || !Range.isCollapsed(selection)) return;

  const currentBlockEntry = Editor.parent(editor, selection);
  if (!currentBlockEntry) return;

  const currentNode = Editor.node(editor, selection.anchor.path);
  if (!currentNode) return;

  const textStart = Editor.start(editor, currentNode[1]);
  const cursorPoint = selection.anchor;

  const rangeUpToCursor: Range = {
    anchor: textStart,
    focus: cursorPoint,
  };

  return Editor.string(editor, rangeUpToCursor);
}

export const convertMark = (
  editor: Editor,
  event: KeyboardEvent,
  path: Path,
  options: MarkdownConvertOptions
): MarkdownRevertOptions | undefined => {
  const {
    match,
    activeMarks,
    triggerCharacter,
    mark,
    revert,
    regex,
    shiftTabRevert,
  } = options;
  if (!mark) return;

  const node = {
    text: match[2],
    ...activeMarks,
    [mark]: true,
  };

  event.preventDefault();

  History.withoutUndoMerging(editor, (editor) => {
    Transforms.delete(editor, {
      distance: match[1].length - triggerCharacter.length,
      unit: 'character',
      reverse: true,
    });
  });
  Editor.insertNode(editor, node);
  Editor.removeMark(editor, mark);

  return {
    type: 'mark',
    textWithMarkdown: match[1],
    plainText: match[2],
    path,
    mark,
    match,
    triggerCharacter,
    revert,
    regex,
    shiftTabRevert,
  };
};

export const convertElement = (
  editor: Editor,
  event: KeyboardEvent,
  path: Path,
  options: MarkdownConvertOptions
): MarkdownRevertOptions | undefined => {
  const {
    match,
    mustBeStartOfBlock,
    blockMustBeEmpty,
    triggerCharacter,
    convert,
    revert,
    regex,
    blockEntry,
    startOfBlock,
    endOfBlock,
    shiftTabRevert,
  } = options;
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return;
  if (!blockEntry) return;

  if (mustBeStartOfBlock && !startOfBlock) {
    // Some markdown plugins require that the markdown shortcut is at
    // the start of the block. e.g. Headings, lists, quotes etc.
    return;
  }

  if (blockMustBeEmpty) {
    // If the block must be empty, we can check this by making sure it
    // contains only one text node and that replacing the text node +
    // trigger character results in an empty string.
    const blockChildren = blockEntry[0].children;

    if (blockChildren.length !== 1) return;
    if (!Text.isText(blockChildren[0])) return;
    if (
      `${blockChildren[0].text}${triggerCharacter}`.replace(regex, '') !== ''
    ) {
      return;
    }
  }

  event.preventDefault();

  History.withoutUndoMerging(editor, (editor) => {
    Transforms.delete(editor, {
      distance: match[1].length - triggerCharacter.length,
      unit: 'character',
      reverse: true,
    });
  });

  const textWithMarkdown = match[1];
  const plainText = match[2];

  convert(editor, {
    ...options,
    startOfBlock,
    endOfBlock,
    blockEntry,
    textWithMarkdown,
    plainText,
  });

  return {
    type: 'element',
    textWithMarkdown,
    plainText,
    previousBlockType: blockEntry[0].type,
    startOfBlock,
    endOfBlock,
    revert,
    path,
    match,
    triggerCharacter,
    regex,
    shiftTabRevert,
  };
};

export const revertMark = (
  editor: Editor,
  revertOptions: MarkdownRevertOptions
) => {
  const { mark, textWithMarkdown, plainText, path } = revertOptions;
  if (!mark) return false;

  const previousEntry = Editor.node(editor, path);
  if (!previousEntry) return false;

  const [, previousPath] = previousEntry;

  Transforms.unsetNodes(editor, mark, { at: previousPath });
  Transforms.delete(editor, {
    distance: plainText.length,
    unit: 'character',
    reverse: true,
  });
  Transforms.insertText(editor, textWithMarkdown);

  return true;
};

export const revertElement = (
  editor: Editor,
  revertOptions: MarkdownRevertOptions
) => {
  revertOptions.revert(editor, revertOptions);
  Transforms.insertText(editor, revertOptions.textWithMarkdown);
  return true;
};
